import { useLingui } from '@lingui/react/macro';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';

import { extendSession } from '#modules/finder/features/posts/model/sessions';
import { invalidateFor, LOCAL_EVENTS } from '#features/realtime/cacheInvalidation';
import { toaster } from '#features/ui/components/Toaster';

/**
 * «Продлить на 30 минут» из меню карточки, комнаты и переписки.
 *
 * После ответа перечитываем списки сессий: комната берёт конец сессии оттуда, и без этого
 * обратный отсчёт и полоса прогресса показывали бы старое время до следующего опроса.
 *
 * Отказ сервера приезжает полем ответа и уже переведён (не автор, упёрлись в восемь часов,
 * у кого-то из команды сразу следом другая сессия) - показываем его как есть.
 */
export const useExtendSession = ({ sessionId, postId }: { sessionId: string; postId: string }) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const mutationKey = ['extend-session', sessionId];
  const mutation = useMutation({ mutationKey, mutationFn: extendSession });
  const isExtending = useIsMutating({ mutationKey }) > 0;

  const extend = async () => {
    if (queryClient.isMutating({ mutationKey }) > 0) return;

    try {
      const result = await mutation.mutateAsync({ sessionId });

      if (result?.__typename === 'Operation') {
        void invalidateFor(queryClient, LOCAL_EVENTS.sessionExtended, {
          parentId: postId,
          targetId: sessionId,
        });
        toaster.create({
          // Без «на 30 минут»: последний шаг упирается в потолок восьми часов и бывает короче.
          title: t`Session extended`,
          type: 'success',
          duration: 3000,
        });
        return;
      }

      toaster.create({
        title:
          result?.__typename === 'GlobalError' && result.errorMessage
            ? result.errorMessage
            : t`Could not extend the session`,
        type: 'error',
        duration: 5000,
      });
    } catch (error) {
      console.error('extend session failed', error);
      toaster.create({ title: t`Could not extend the session`, type: 'error', duration: 5000 });
    }
  };

  return { extend, isExtending };
};
